
import { useEffect, useState, useCallback } from "react";
import { Loader2, UserCheck, Plus, FileSpreadsheet, Trash2 } from "lucide-react";
import { Confirmado } from "@/types/participante";
import { listarConfirmados, removerConfirmado } from "@/lib/participantes";
import AdicionarParticipanteModal from "./AdicionarParticipanteModal";
import ImportarExcelModal from "./ImportarExcelModal";
import { formatarCPF, formatarTelefone } from "@/lib/masks";

const origemLabel: Record<string, { label: string; cls: string }> = {
  manual: { label: "Manual", cls: "bg-blue-50 text-blue-600" },
  excel: { label: "Excel", cls: "bg-green-50 text-green-700" },
  listaEspera: { label: "Lista de espera", cls: "bg-orange-50 text-orange-600" },
};

interface Props {
  edicaoId: string;
}

export default function ListaConfirmadosTab({ edicaoId }: Props) {
  const [lista, setLista] = useState<Confirmado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [removendo, setRemovendoId] = useState<string | null>(null);
  const [modalManual, setModalManual] = useState(false);
  const [modalExcel, setModalExcel] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLista(await listarConfirmados(edicaoId));
    } finally {
      setCarregando(false);
    }
  }, [edicaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleRemover = async (c: Confirmado) => {
    const ok = window.confirm(`Remover "${c.nomeCompleto}" dos confirmados?`);
    if (!ok) return;
    setRemovendoId(c.id);
    try {
      await removerConfirmado(edicaoId, c.id);
      await carregar();
    } finally {
      setRemovendoId(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-5 h-5 animate-spin text-black/20" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header com ações */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-black/40">
            {lista.length > 0
              ? `${lista.length} confirmado${lista.length !== 1 ? "s" : ""}`
              : "Nenhum confirmado ainda"}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setModalExcel(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-black/10 text-sm font-medium text-black/60 hover:text-black hover:border-black/20 hover:bg-black/[0.02] transition"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Importar Excel
            </button>
            <button
              onClick={() => setModalManual(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition"
            >
              <Plus className="w-4 h-4" />
              Adicionar
            </button>
          </div>
        </div>

        {/* Lista vazia */}
        {lista.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 p-16 flex flex-col items-center justify-center text-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-black/20" />
            </div>
            <p className="text-sm font-medium text-black/50">
              Nenhum confirmado cadastrado.
            </p>
            <p className="text-xs text-black/30">
              Adicione manualmente, importe um Excel ou confirme alguém da lista de espera.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/5">
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    #
                  </th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    CPF
                  </th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    E-mail
                  </th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    WhatsApp
                  </th>
                  <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                    Origem
                  </th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {lista.map((c, i) => {
                  const origem = origemLabel[c.origem] ?? {
                    label: c.origem,
                    cls: "bg-black/5 text-black/40",
                  };
                  return (
                    <tr key={c.id} className="hover:bg-black/[0.02] transition group">
                      <td className="px-5 py-4 text-black/30 text-xs">{i + 1}</td>
                      <td className="px-5 py-4 font-medium text-black">{c.nomeCompleto}</td>
                      <td className="px-5 py-4 text-black/50 font-mono text-xs">
                        {formatarCPF(c.cpf)}
                      </td>
                      <td className="px-5 py-4 text-black/50 text-xs">
                        {c.email || "-"}
                      </td>
                      <td className="px-5 py-4 text-black/50 text-xs">
                          {formatarTelefone(c.whatsapp)}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${origem.cls}`}
                        >
                          {origem.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end opacity-0 group-hover:opacity-100 transition">
                          <button
                            onClick={() => handleRemover(c)}
                            disabled={removendo === c.id}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-50"
                          >
                            {removendo === c.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalManual && (
        <AdicionarParticipanteModal
          edicaoId={edicaoId}
          onClose={() => setModalManual(false)}
          onSaved={() => {
            setModalManual(false);
            carregar();
          }}
        />
      )}

      {modalExcel && (
        <ImportarExcelModal
          edicaoId={edicaoId}
          onClose={() => setModalExcel(false)}
          onImportado={() => {
            setModalExcel(false);
            carregar();
          }}
        />
      )}
    </>
  );
}
